import { useQuery, useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BoardApi } from "../../../services/board-service-api";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import { activateOnKey } from "./a11y";
import "./board-backlog-view.scss";
import { boardKeys } from "./board-query-keys";
import { buildBoardPath } from "./board-routing";
import { BoardService } from "./board-service";
import BurndownChart from "./burndown-chart";
import { reportsChanged } from "./change-event-routing";
import { confirmDialog, promptDialog } from "./confirm-service";
import TypeIcon from "./type-icon";
import {
  Board,
  BoardChangeEvent,
  BoardStatus,
  IssueCard,
  Project,
  Sprint,
  SprintBurndown,
  STATUS_DOT_COLOR,
  boardProjectKeys
} from "./types";
import { ColumnFilters, useColumnIssues } from "./use-column-issues";

interface SprintBurndownInlineProps {
  sprintId: string;
}

// Compact burndown for a started sprint in the backlog. Self-contained: fetches its own series and
// refreshes it when issues change, so it can be dropped into any sprint section.
const SprintBurndownInline: React.FC<SprintBurndownInlineProps> = ({ sprintId }) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: boardKeys.burndown(sprintId),
    queryFn: (): Promise<SprintBurndown> => BoardApi.getSprintBurndown(sprintId)
  });
  useEffect(() => {
    return BoardService.subscribe((event: BoardChangeEvent) => {
      if (reportsChanged(event)) {
        void queryClient.invalidateQueries({ queryKey: boardKeys.burndown(sprintId) });
      }
    });
  }, [queryClient, sprintId]);
  return <BurndownChart burndown={query.data ?? null} loading={query.isLoading} compact />;
};

export interface BoardBacklogViewProps {
  board: Board;
  boardId: string;
  filters: ColumnFilters;
  sprints: Sprint[];
  statuses: BoardStatus[];
  projects: Project[];
  // Workspace owner — may create/start/complete/delete sprints. Non-owners can still assign
  // issues to existing sprints (issue-level), just not manage the sprints themselves.
  canManage: boolean;
}

interface PlanRowProps {
  issue: IssueCard;
  status: BoardStatus | undefined;
  project: Project | undefined;
  showProject: boolean;
  onOpen: () => void;
  onAssign: (sprintId: string | null) => void;
  sprints: Sprint[];
  currentSprintId: string | null;
}

const PlanRow: React.FC<PlanRowProps> = ({
  issue,
  status,
  project,
  showProject,
  onOpen,
  onAssign,
  sprints,
  currentSprintId
}) => {
  return (
    <div
      className={classNames("kuika-board-backlog__row", OVERFLOW_REVEAL_HOST_CLASS)}
      onClick={onOpen}
      onKeyDown={activateOnKey(onOpen)}
      role="button"
      tabIndex={0}
    >
      <TypeIcon type={issue.type} />
      <span className="kuika-board-backlog__row-key">{issue.key}</span>
      {showProject && project && (
        <span className="kuika-board-backlog__row-project">
          <span className="kuika-board-backlog__row-project-dot" style={{ background: project.color }} />
          {project.key}
        </span>
      )}
      <span className="kuika-board-backlog__row-summary">
        <KuikaOverflowText>{issue.summary}</KuikaOverflowText>
      </span>
      <span className="kuika-board-backlog__row-spacer" />
      {issue.storyPoints != null && <span className="kuika-board-backlog__row-points">{issue.storyPoints}</span>}
      <span className="kuika-board-backlog__row-status">
        <span
          className="kuika-board-backlog__row-status-dot"
          style={{ background: STATUS_DOT_COLOR[status?.category ?? "todo"] }}
        />
        {status?.name ?? "—"}
      </span>
      {sprints.length > 0 && (
        <select
          className="kuika-board-backlog__row-assign"
          value={currentSprintId ?? "__backlog__"}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
            onAssign(event.target.value === "__backlog__" ? null : event.target.value)
          }
          aria-label={`Move ${issue.key}`}
        >
          <option value="__backlog__">Backlog</option>
          {sprints.map((sprint: Sprint) => (
            <option key={sprint.id} value={sprint.id}>
              {sprint.name}
              {sprint.state === "active" ? " · active" : ""}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};

const BoardBacklogView: React.FC<BoardBacklogViewProps> = ({
  board,
  boardId,
  filters,
  sprints,
  statuses,
  projects,
  canManage
}) => {
  const navigate = useNavigate();
  const { domainName } = useParams<{ domainName: string }>();
  const allowedKeys: Set<string> = useMemo(() => new Set(boardProjectKeys(board)), [board]);
  const showProject: boolean = board.scope.kind === "multi_project";
  const isScrum: boolean = (board.mode ?? "scrum") === "scrum";

  // Board-wide server query (every column); filters are server-side. Sprint/backlog grouping
  // is over the loaded rows — scrolling the backlog loads more. Totals reflect loaded issues.
  const { items, hasMore, loading, loadMore } = useColumnIssues(boardId, "", [], filters);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel: HTMLDivElement | null = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer: IntersectionObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        if (entries.some((entry: IntersectionObserverEntry) => entry.isIntersecting)) loadMore();
      },
      { rootMargin: "300px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const activeSprints: Sprint[] = useMemo(
    () => sprints.filter((sprint: Sprint) => sprint.state !== "closed" && allowedKeys.has(sprint.projectKey)),
    [sprints, allowedKeys]
  );

  // Server already scopes to the board's projects and excludes archived/subtasks.
  const filtered: IssueCard[] = items;

  const backlog: IssueCard[] = useMemo(
    () => filtered.filter((issue: IssueCard) => issue.sprintId === null),
    [filtered]
  );

  const issuesBySprint: Map<string, IssueCard[]> = useMemo(() => {
    const map: Map<string, IssueCard[]> = new Map();
    for (const sprint of activeSprints) map.set(sprint.id, []);
    for (const issue of filtered) {
      if (issue.sprintId && map.has(issue.sprintId)) {
        map.get(issue.sprintId)!.push(issue);
      }
    }
    return map;
  }, [filtered, activeSprints]);

  const handleAssign = (issueId: string, sprintId: string | null): void => {
    BoardService.assignIssueToSprint(issueId, sprintId);
  };

  const handleStartSprint = (sprint: Sprint): void => {
    void promptDialog({
      title: "Start sprint",
      message: `Start ${sprint.name}. Leave the date empty for no fixed end.`,
      label: "End date (optional)",
      placeholder: "YYYY-MM-DD",
      confirmLabel: "Start",
      allowEmpty: true,
      intent: "edit",
      iconName: "calendar-event"
    }).then((endDate: string | null) => {
      if (endDate === null) return; // cancelled
      BoardService.startSprint(sprint.id, endDate ? new Date(endDate).toISOString() : null);
    });
  };

  const handleCompleteSprint = (sprint: Sprint): void => {
    void confirmDialog({
      title: "Complete sprint",
      message: `Complete ${sprint.name}? Unfinished issues go back to backlog.`,
      confirmLabel: "Complete",
      intent: "warning"
    }).then((ok: boolean) => {
      if (ok) BoardService.completeSprint(sprint.id);
    });
  };

  const handleDeleteSprint = (sprint: Sprint): void => {
    void confirmDialog({
      title: "Delete sprint",
      message: `Delete ${sprint.name}? Its issues move to the backlog.`,
      confirmLabel: "Delete",
      danger: true
    }).then((ok: boolean) => {
      if (ok) BoardService.deleteSprint(sprint.id);
    });
  };

  const handleNewSprint = (projectKey: string): void => {
    void promptDialog({
      title: `New sprint · ${projectKey}`,
      label: "Sprint name",
      defaultValue: `Sprint ${sprints.filter((s: Sprint) => s.projectKey === projectKey).length + 1}`,
      confirmLabel: "Create"
    }).then((name: string | null) => {
      if (!name) return;
      BoardService.createSprint(projectKey, name, "");
    });
  };

  const open = (issue: IssueCard): void => navigate(`${buildBoardPath(domainName ?? "", board)}/${issue.key}`);

  const renderSprintSection = (sprint: Sprint): React.ReactNode => {
    const sprintIssues: IssueCard[] = issuesBySprint.get(sprint.id) ?? [];
    const doneStatusIds: Set<string> = new Set(
      statuses.filter((s: BoardStatus) => s.category === "done").map((s: BoardStatus) => s.id)
    );
    const completed: number = sprintIssues.filter((entry: IssueCard) => doneStatusIds.has(entry.statusId)).length;
    const totalPoints: number = sprintIssues.reduce(
      (sum: number, entry: IssueCard) => sum + (entry.storyPoints ?? 0),
      0
    );
    const donePoints: number = sprintIssues
      .filter((entry: IssueCard) => doneStatusIds.has(entry.statusId))
      .reduce((sum: number, entry: IssueCard) => sum + (entry.storyPoints ?? 0), 0);
    const inProgressPoints: number = sprintIssues
      .filter((entry: IssueCard) => {
        const status: BoardStatus | undefined = statuses.find((s: BoardStatus) => s.id === entry.statusId);
        return status?.category === "in_progress";
      })
      .reduce((sum: number, entry: IssueCard) => sum + (entry.storyPoints ?? 0), 0);
    const project: Project | undefined = projects.find((entry: Project) => entry.key === sprint.projectKey);

    return (
      <section
        key={sprint.id}
        className={classNames("kuika-board-backlog__section", {
          "kuika-board-backlog__section--active": sprint.state === "active"
        })}
      >
        <header className="kuika-board-backlog__section-header">
          <div className="kuika-board-backlog__section-title-block">
            <span
              className="kuika-board-backlog__section-dot"
              style={{ background: project?.color ?? "var(--dark-4)" }}
            />
            <span className="kuika-board-backlog__section-title">{sprint.name}</span>
            <span
              className={classNames(
                "kuika-board-backlog__section-state",
                `kuika-board-backlog__section-state--${sprint.state}`
              )}
            >
              {sprint.state}
            </span>
            {showProject && project && <span className="kuika-board-backlog__section-project">{project.key}</span>}
            <span className="kuika-board-backlog__section-meta">
              {sprintIssues.length} {sprintIssues.length === 1 ? "issue" : "issues"} · {completed} done · {totalPoints}{" "}
              pts
              {sprint.endDate && ` · ends ${dayjs(sprint.endDate).format("MMM D")}`}
            </span>
          </div>
          {canManage && (
            <div className="kuika-board-backlog__section-actions">
              {sprint.state === "future" && (
                <KuikaButton type="secondary" size="sm" onClick={() => handleStartSprint(sprint)}>
                  Start sprint
                </KuikaButton>
              )}
              {sprint.state === "active" && (
                <KuikaButton type="secondary" size="sm" onClick={() => handleCompleteSprint(sprint)}>
                  Complete sprint
                </KuikaButton>
              )}
              <KuikaTooltip content="Delete sprint" position="bottom">
                <KuikaButton
                  type="tertiary"
                  color="neutral"
                  size="sm"
                  isIcon
                  onClick={() => handleDeleteSprint(sprint)}
                >
                  <KuikaSvg name="trash" width={14} height={14} />
                </KuikaButton>
              </KuikaTooltip>
            </div>
          )}
        </header>
        {totalPoints > 0 && (
          <div className="kuika-board-backlog__capacity">
            <div className="kuika-board-backlog__capacity-bar">
              <span
                className="kuika-board-backlog__capacity-fill kuika-board-backlog__capacity-fill--done"
                style={{ width: `${(donePoints / totalPoints) * 100}%` }}
              />
              <span
                className="kuika-board-backlog__capacity-fill kuika-board-backlog__capacity-fill--progress"
                style={{ width: `${(inProgressPoints / totalPoints) * 100}%` }}
              />
            </div>
            <div className="kuika-board-backlog__capacity-legend">
              <span>
                <span className="kuika-board-backlog__capacity-dot kuika-board-backlog__capacity-dot--done" />
                {donePoints} done
              </span>
              <span>
                <span className="kuika-board-backlog__capacity-dot kuika-board-backlog__capacity-dot--progress" />
                {inProgressPoints} in progress
              </span>
              <span>
                <span className="kuika-board-backlog__capacity-dot kuika-board-backlog__capacity-dot--todo" />
                {totalPoints - donePoints - inProgressPoints} to do
              </span>
              <span style={{ marginLeft: "auto", color: "var(--dark-6)" }}>{totalPoints} pts total</span>
            </div>
          </div>
        )}
        {sprint.state === "active" && (
          <div className="kuika-board-backlog__burndown">
            <SprintBurndownInline sprintId={sprint.id} />
          </div>
        )}
        {sprint.goal && <div className="kuika-board-backlog__goal">{sprint.goal}</div>}
        <div className="kuika-board-backlog__rows">
          {sprintIssues.length === 0 && (
            <div className="kuika-board-backlog__empty">Drag in from backlog or assign via the row menu.</div>
          )}
          {sprintIssues.map((issue: IssueCard) => (
            <PlanRow
              key={issue.id}
              issue={issue}
              status={statuses.find((entry: BoardStatus) => entry.id === issue.statusId)}
              project={projects.find((entry: Project) => entry.key === issue.projectKey)}
              showProject={showProject}
              onOpen={() => open(issue)}
              onAssign={(sprintId: string | null) => handleAssign(issue.id, sprintId)}
              sprints={activeSprints}
              currentSprintId={issue.sprintId}
            />
          ))}
        </div>
      </section>
    );
  };

  return (
    <div className="kuika-board-backlog">
      {isScrum && activeSprints.map(renderSprintSection)}

      <section className="kuika-board-backlog__section kuika-board-backlog__section--backlog">
        <header className="kuika-board-backlog__section-header">
          <div className="kuika-board-backlog__section-title-block">
            <span className="kuika-board-backlog__section-title">Backlog</span>
            <span className="kuika-board-backlog__section-meta">
              {backlog.length} {backlog.length === 1 ? "issue" : "issues"}
            </span>
          </div>
          {isScrum && canManage && (
            <div className="kuika-board-backlog__section-actions">
              {boardProjectKeys(board).map((projectKey: string) => (
                <KuikaButton key={projectKey} type="secondary" size="sm" onClick={() => handleNewSprint(projectKey)}>
                  <KuikaSvg name="plus" width={12} height={12} />
                  <span style={{ marginLeft: 4 }}>Sprint{showProject ? ` · ${projectKey}` : ""}</span>
                </KuikaButton>
              ))}
            </div>
          )}
        </header>
        <div className="kuika-board-backlog__rows">
          {backlog.length === 0 && (
            <div className="kuika-board-backlog__empty">
              Backlog is empty. Use "+ New" in the toolbar to add issues.
            </div>
          )}
          {backlog.map((issue: IssueCard) => (
            <PlanRow
              key={issue.id}
              issue={issue}
              status={statuses.find((entry: BoardStatus) => entry.id === issue.statusId)}
              project={projects.find((entry: Project) => entry.key === issue.projectKey)}
              showProject={showProject}
              onOpen={() => open(issue)}
              onAssign={(sprintId: string | null) => handleAssign(issue.id, sprintId)}
              sprints={isScrum ? activeSprints : []}
              currentSprintId={issue.sprintId}
            />
          ))}
          {(hasMore || loading) && (
            <div ref={sentinelRef}>
              {loading && (
                <div className="kuika-board__column-loading-more" role="status" aria-label="Loading more issues">
                  <span className="kuika-board__column-loading-bar" />
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BoardBacklogView;
